import { useCallback, useMemo } from 'react'

import { JourneyOutcome } from '@cash-web/protos/squareup/cash/blockuserjourneys/api/v1/service.pb'
import { useUserJourneyContext } from '@cash-web/shared-user-journey-data-access-context'
import { Event, UserJourneyEventMonitor } from '@squareup/customer-data-events-web'

// Mapping from string outcomes to JourneyOutcome enum values
const OUTCOME_MAPPING: Record<string, JourneyOutcome> = {
  succeeded: JourneyOutcome.JOURNEY_OUTCOME_SUCCEEDED,
  failed: JourneyOutcome.JOURNEY_OUTCOME_FAILED,
  userCanceled: JourneyOutcome.JOURNEY_OUTCOME_USER_CANCELED,
  processCrashed: JourneyOutcome.JOURNEY_OUTCOME_PROCESS_CRASHED,
  processKilled: JourneyOutcome.JOURNEY_OUTCOME_PROCESS_KILLED,
  terminated: JourneyOutcome.JOURNEY_OUTCOME_TERMINATED,
}

/**
 * Hook that provides a function to process CDF events and automatically trigger
 * user journey actions based on the provided event monitors. This is meant to be
 * used directly in the CDF event library when events are being logged.
 *
 */
export const useUserJourneyCdfEventMonitoring = (userJourneyEventMonitors: ReadonlyArray<UserJourneyEventMonitor>) => {
  const userJourneyContext = useUserJourneyContext()

  const monitorsByEventName = useMemo(() => {
    if (!userJourneyContext.enabled) {
      return new Map<string, UserJourneyEventMonitor[]>()
    }

    const monitorsMap = new Map<string, UserJourneyEventMonitor[]>()

    userJourneyEventMonitors.forEach(monitor => {
      const eventName = monitor.cdfEvent

      if (!monitorsMap.has(eventName)) {
        monitorsMap.set(eventName, [])
      }
      monitorsMap.get(eventName)?.push(monitor)
    })

    return monitorsMap
  }, [userJourneyEventMonitors, userJourneyContext.enabled])

  const processCdfEvent = useCallback(
    (cdfEvent: Event) => {
      if (!userJourneyContext.enabled) {
        return
      }

      const matchingMonitors = monitorsByEventName.get(cdfEvent.name?.replace(/ /g, '') ?? '')

      if (matchingMonitors) {
        matchingMonitors.forEach(monitor => {
          if (doParametersMatch(cdfEvent.parameters, monitor.requiredParameters)) {
            handleEventMatch(monitor, userJourneyContext)
          }
        })
      }
    },
    [monitorsByEventName, userJourneyContext]
  )

  return { processCdfEvent }
}

/**
 * Check if event parameters match the required parameters for a monitor
 */
function doParametersMatch(
  eventParameters?: Event['parameters'],
  requiredParameters?: Record<string, string>
): boolean {
  if (!requiredParameters || Object.keys(requiredParameters).length === 0) {
    return true
  }

  // All required parameters must match exactly
  return Object.entries(requiredParameters).every(([key, value]) => {
    return eventParameters?.[key] === value
  })
}

/**
 * Handle when a parsed CDF event matches a journey event monitor
 */
function handleEventMatch(
  monitor: UserJourneyEventMonitor,
  userJourneyContext: ReturnType<typeof useUserJourneyContext>
) {
  const { journeyName, trigger } = monitor

  switch (trigger.type) {
    case 'entry':
      userJourneyContext.startJourney(journeyName)
      break

    case 'variant':
      userJourneyContext.setVariant(journeyName, trigger.variantName)
      break

    case 'friction':
      userJourneyContext.addFriction(journeyName, trigger.signalName)
      break

    case 'exit': {
      const mappedOutcome = OUTCOME_MAPPING[trigger.outcome] || JourneyOutcome.JOURNEY_OUTCOME_UNSPECIFIED
      userJourneyContext.completeJourney(journeyName, mappedOutcome)
      break
    }

    case 'tag':
      userJourneyContext.addTag(journeyName, trigger.tagName)
      break

    case 'frustration':
      userJourneyContext.addFrustration(journeyName, trigger.signalName)
      break
  }
}
